export type ProductionJobUpdate = {
  jobId: number
  status: string
  progress: number
  step: string | null
}

export type PrepareContentProductionInput = {
  sellerId?: number | string | null
  customerId?: number | string | null

  projectId: number

  plan: any
  creativeDirection: any
  discovery?: any

  script: any
  scriptGenerationId: number

  productImageUrl?: string | null

  generateStoryboardImages: boolean
  allowHyperframesScenes?: boolean

  requestPayload?: Record<string, any>

  onProgress?: (
    update: {
      stage:
        | 'updating_project'
        | 'creating_shell'
        | 'starting_storyboard'
        | 'generating_storyboard'
        | 'preparing_scenes'
        | 'completed'
      label: string
      status?: string
      progress: number
      jobId?: number | null
      jobStep?: string | null
    }
  ) => void
}

export type PreparedContentProduction = {
  projectId: number

  generationId: number
  projectJson: any

  premiumStoryboardGeneration: any
  preparedStoryboardPlan: any

  generatedScenes: any[]

  approvedPlan: any
  creativeDirection: any

  productImageUrl: string | null

  needsAvatar: boolean
  needsProductUpload: boolean

  nextPhase:
    | 'product_input'
    | 'creating_character'
    | 'generating_scenes'
}

function absoluteUrl(
  value?: string | null
): string | null {
  const clean = String(value || '').trim()

  if (!clean) {
    return null
  }

  if (
    typeof window === 'undefined'
  ) {
    return clean
  }

  return new URL(
    clean,
    window.location.origin
  ).toString()
}

function buildApprovedPlan(
  basePlan: any,
  creativeDirection: any
) {
  const durationTargetSec =
    Number(
      creativeDirection
        ?.durationTargetSec ||
        basePlan
          ?.durationTargetSec ||
        60
    )

  const productionMode =
    creativeDirection
      ?.productionMode ||
    basePlan
      ?.productionMode ||
    (
      durationTargetSec >= 300
        ? 'cinematic_longform'
        : 'express'
    )

  const visualOutputMode =
    creativeDirection
      ?.visualOutputMode ||
    basePlan
      ?.visualOutputMode ||
    (
      durationTargetSec >= 300
        ? 'static_images'
        : 'mixed'
    )

  return {
    ...(basePlan || {}),

    durationMode:
      creativeDirection
        ?.durationMode ||
      basePlan
        ?.durationMode ||
      'auto',

    durationTargetSec,

    productionMode,

    visualOutputMode,
  }
}

async function updateProjectForProduction(input: {
  projectId: number
  approvedPlan: any
  creativeDirection: any
}) {
  const {
    projectId,
    approvedPlan,
    creativeDirection,
  } = input

  const res = await fetch(
    `/api/creator-studio/projects/${projectId}`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type':
          'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        mode: 'quick',
        status: 'active',

        /*
         * Mantén "express", porque eso es
         * exactamente lo que utiliza ahora
         * createCreatorStudioProjectFromPlan().
         */
        currentStep: 'express',

        title:
          approvedPlan?.title ||
          'Nuevo proyecto creativo',

        objective:
          approvedPlan?.objective ||
          '',

        brief: {
          ...(approvedPlan || {}),

          title:
            approvedPlan?.title ||
            'Nuevo proyecto creativo',

          objective:
            approvedPlan?.objective ||
            '',

          hook:
            approvedPlan?.hook ||
            '',

          cta:
            approvedPlan?.cta ||
            '',

          productName:
            approvedPlan
              ?.productName ||
            '',

          productImageUrl:
            approvedPlan
              ?.productImageUrl ||
            null,

          audience:
            approvedPlan?.audience ||
            '',

          language:
            approvedPlan?.language ||
            'es',

          aspectRatio:
            approvedPlan
              ?.aspectRatio ||
            '9:16',

          durationMode:
            approvedPlan
              ?.durationMode ||
            creativeDirection
              ?.durationMode ||
            'auto',

          durationTargetSec:
            Number(
              approvedPlan
                ?.durationTargetSec ||
              60
            ),

          productionMode:
            approvedPlan
              ?.productionMode ||
            creativeDirection
              ?.productionMode ||
            'express',

          visualOutputMode:
            approvedPlan
              ?.visualOutputMode ||
            creativeDirection
              ?.visualOutputMode ||
            'mixed',

          goal:
            approvedPlan?.goal ||
            creativeDirection
              ?.contentGoal ||
            'viral',

          style:
            approvedPlan?.style ||
            '',

          musicMood:
            approvedPlan
              ?.musicMood ||
            '',

          voiceStyle:
            approvedPlan
              ?.voiceStyle ||
            '',
        },

        creativeDirection,
      }),
    }
  )

  const json =
    await res.json().catch(() => null)

  if (!res.ok || !json?.ok) {
    throw new Error(
      json?.error ||
        'No se pudo actualizar el proyecto.'
    )
  }

  return json
}

async function createProductionShell(input: {
  sellerId?: number | string | null
  customerId?: number | string | null

  projectId: number

  approvedPlan: any
  creativeDirection: any
  discovery?: any

  script: any
  scriptGenerationId: number

  productImageUrl?: string | null

  requestPayload?: Record<string, any>

  allowHyperframesScenes: boolean
}) {
  const res = await fetch(
    '/api/task-flows/create-content/project-json',
    {
      method: 'POST',
      headers: {
        'Content-Type':
          'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        sellerId:
          input.sellerId,

        customerId:
          input.customerId,

        projectId:
          input.projectId,

        mode:
          'production_shell_only',

        sourceScriptGenerationId:
          input.scriptGenerationId,

        payload: {
          ...(input.requestPayload || {}),

          projectId:
            input.projectId,

          durationMode:
            input.approvedPlan
              ?.durationMode,

          durationTargetSec:
            input.approvedPlan
              ?.durationTargetSec,

          productionMode:
            input.approvedPlan
              ?.productionMode,

          visualOutputMode:
            input.approvedPlan
              ?.visualOutputMode,

          sourceScriptGenerationId:
            input.scriptGenerationId,

          scriptPackage:
            input.script,

          creativeDirection:
            input.creativeDirection,

          productImageUrl:
            input.productImageUrl ||
            null,

          allowHyperframesScenes:
            input.allowHyperframesScenes,

          forceRealScenes:
            !input.allowHyperframesScenes,

          disallowMotionGraphicsScenes:
            !input.allowHyperframesScenes,

          discovery: {
            ...(input.requestPayload
              ?.discovery || {}),

            ...(input.discovery || {}),

            source:
              input.requestPayload
                ?.source ||
              'samantha_chat',
          },
        },

        plan:
          input.approvedPlan,
      }),
    }
  )

  const json =
    await res.json().catch(() => null)

  if (
    !res.ok ||
    !json?.ok ||
    !json?.generationId ||
    !json?.projectJson
  ) {
    throw new Error(
      json?.error ||
        'No se pudo crear el contenedor de producción.'
    )
  }

  return {
    generationId:
      Number(json.generationId),

    projectJson:
      json.projectJson,
  }
}

async function startStoryboardJob(input: {
  projectId: number
  scriptGenerationId: number
  productionGenerationId: number
  allowHyperframesScenes: boolean
}) {
  const res = await fetch(
    '/api/creator-studio/jobs/storyboard/start',
    {
      method: 'POST',
      headers: {
        'Content-Type':
          'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        inputs: {
          projectId:
            input.projectId,

          scriptGenerationId:
            input.scriptGenerationId,

          productionGenerationId:
            input.productionGenerationId,

          allowHyperframesScenes:
            input.allowHyperframesScenes,

          forceRealScenes:
            !input.allowHyperframesScenes,

          disallowMotionGraphicsScenes:
            !input.allowHyperframesScenes,
        },
      }),
    }
  )

  const json =
    await res.json().catch(() => null)

  if (!res.ok || !json?.ok) {
    throw new Error(
      json?.error ||
        'No se pudo iniciar el storyboard.'
    )
  }

  const jobId =
    Number(json.jobId || 0)

  if (!jobId) {
    throw new Error(
      'No se recibió el jobId del storyboard.'
    )
  }

  return {
    jobId,

    status:
      String(
        json.status || 'queued'
      ),

    progress:
      Number(json.progress || 0),

    step:
      json.step ||
      'Storyboard en cola',
  }
}

async function pollStoryboardJob(input: {
  jobId: number

  onUpdate?: (
    update: ProductionJobUpdate
  ) => void
}) {
  let attempts = 0

  while (true) {
    attempts += 1

    const res = await fetch(
      `/api/creator-studio/jobs/${input.jobId}`,
      {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
      }
    )

    const data =
      await res.json().catch(() => null)

    if (!res.ok || !data?.ok) {
      throw new Error(
        data?.error ||
          'No se pudo consultar el storyboard.'
      )
    }

    const job =
      data.job || {}

    input.onUpdate?.({
      jobId:
        input.jobId,

      status:
        String(
          job.status || 'running'
        ),

      progress:
        Number(job.progress || 0),

      step:
        job.step || null,
    })

    if (
      job.status ===
      'completed'
    ) {
      const generation =
        data.generation || null

      if (
        !generation
          ?.generation_id ||
        !generation
          ?.result_json
      ) {
        throw new Error(
          'El storyboard terminó, pero no devolvió una generación válida.'
        )
      }

      return generation
    }

    if (
      job.status ===
      'failed'
    ) {
      throw new Error(
        job.errorMessage ||
          'No se pudo generar el storyboard premium.'
      )
    }

    if (
      job.status ===
      'cancelled'
    ) {
      throw new Error(
        'La generación del storyboard fue cancelada.'
      )
    }

    const delay =
      attempts < 10
        ? 2000
        : attempts < 30
          ? 5000
          : 10000

    await new Promise(
      (resolve) =>
        window.setTimeout(
          resolve,
          delay
        )
    )
  }
}

async function generateStoryboardScenes(input: {
  generationId: number

  sellerId?: number | string | null
  customerId?: number | string | null

  projectJson: any
  preparedPlan: any

  premiumStoryboardGenerationId?: number | null

  productImageUrl?: string | null

  generateStoryboardImages: boolean
  allowHyperframesScenes: boolean
}) {
  const res = await fetch(
    '/api/creator-studio/video/storyboard/generate',
    {
      method: 'POST',
      headers: {
        'Content-Type':
          'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        generationId:
          input.generationId,

        customerId:
          input.customerId,

        sellerId:
          input.sellerId,

        brief: {
          ...(input.projectJson
            ?.brief || {}),

          initialImage:
            input.productImageUrl ||
            null,

          productImageUrl:
            input.productImageUrl ||
            null,
        },

        preparedPlan:
          input.preparedPlan,

        premiumStoryboardPlan:
          input.preparedPlan,

        sourcePremiumStoryboardGenerationId:
          input
            .premiumStoryboardGenerationId ||
          null,

        skipFal:
          !input
            .generateStoryboardImages,

        allowHyperframesScenes:
          input.allowHyperframesScenes,

        forceRealScenes:
          !input.allowHyperframesScenes,

        disallowMotionGraphicsScenes:
          !input.allowHyperframesScenes,

        notifications: {
          channels: ['dashboard'],
          requireApproval: true,
        },
      }),
    }
  )

  const json =
    await res.json().catch(() => null)

  if (!res.ok || !json?.ok) {
    throw new Error(
      json?.error ||
        'No se pudo preparar el storyboard visual.'
    )
  }

  return Array.isArray(json.scenes)
    ? json.scenes
    : []
}

export async function prepareContentProduction(
  input: PrepareContentProductionInput
): Promise<PreparedContentProduction> {
  const allowHyperframesScenes =
    input.allowHyperframesScenes ===
    true

  const productImageUrl =
    absoluteUrl(
      input.productImageUrl
    )

  const approvedPlan =
    buildApprovedPlan(
      {
        ...(input.plan || {}),

        productImageUrl,
      },
      input.creativeDirection
    )

  input.onProgress?.({
    stage:
      'updating_project',

    label:
      'Actualizando proyecto',

    progress:
      5,
  })

  await updateProjectForProduction({
    projectId:
      input.projectId,

    approvedPlan,

    creativeDirection:
      input.creativeDirection,
  })

  input.onProgress?.({
    stage:
      'creating_shell',

    label:
      'Preparando contenedor de producción',

    progress:
      15,
  })

  const shell =
    await createProductionShell({
      sellerId:
        input.sellerId,

      customerId:
        input.customerId,

      projectId:
        input.projectId,

      approvedPlan,

      creativeDirection:
        input.creativeDirection,

      discovery:
        input.discovery,

      script:
        input.script,

      scriptGenerationId:
        input.scriptGenerationId,

      productImageUrl,

      requestPayload:
        input.requestPayload,

      allowHyperframesScenes,
    })

  input.onProgress?.({
    stage:
      'starting_storyboard',

    label:
      'Iniciando storyboard premium',

    progress:
      25,
  })

  const started =
    await startStoryboardJob({
      projectId:
        input.projectId,

      scriptGenerationId:
        input.scriptGenerationId,

      productionGenerationId:
        shell.generationId,

      allowHyperframesScenes,
    })

  input.onProgress?.({
    stage:
      'generating_storyboard',

    label:
      started.step ||
      'Storyboard en cola',

    status:
      started.status,

    progress:
      Math.max(
        25,
        Number(
          started.progress || 0
        )
      ),

    jobId:
      started.jobId,

    jobStep:
      started.step,
  })

  const premiumStoryboardGeneration =
    await pollStoryboardJob({
      jobId:
        started.jobId,

      onUpdate:
        (job) => {
          /*
           * Reservamos 25–80% para el job.
           */
          const mappedProgress =
            25 +
            Math.round(
              Math.max(
                0,
                Math.min(
                  100,
                  job.progress
                )
              ) * 0.55
            )

          input.onProgress?.({
            stage:
              'generating_storyboard',

            label:
              job.step ||
              'Generando storyboard premium',

            status:
              job.status,

            progress:
              mappedProgress,

            jobId:
              job.jobId,

            jobStep:
              job.step,
          })
        },
    })

  const preparedStoryboardPlan =
    premiumStoryboardGeneration
      ?.result_json ||
    null

  const goal =
    String(
      shell.projectJson
        ?.brief?.goal ||
      approvedPlan?.goal ||
      input.creativeDirection
        ?.contentGoal ||
      ''
    )
      .trim()
      .toLowerCase()

  const needsAvatar =
    goal === 'ugc'

  const expectsProductImage =
    (
      input.creativeDirection
        ?.hasProductImage ===
      'yes'
    )

  const needsProductUpload =
    expectsProductImage &&
    !productImageUrl

  let generatedScenes: any[] = []

  /*
   * Si falta producto o avatar todavía no
   * generamos las escenas. Eso lo continuará
   * SynCreateContentFlow después del input visual.
   */
  if (
    !needsProductUpload &&
    !needsAvatar
  ) {
    input.onProgress?.({
      stage:
        'preparing_scenes',

      label:
        input.generateStoryboardImages
          ? 'Generando imágenes del storyboard'
          : 'Preparando estructura del storyboard',

      progress:
        85,
    })

    generatedScenes =
      await generateStoryboardScenes({
        generationId:
          shell.generationId,

        sellerId:
          input.sellerId,

        customerId:
          input.customerId,

        projectJson:
          shell.projectJson,

        preparedPlan:
          preparedStoryboardPlan,

        premiumStoryboardGenerationId:
          Number(
            premiumStoryboardGeneration
              ?.generation_id ||
            0
          ) || null,

        productImageUrl,

        generateStoryboardImages:
          input.generateStoryboardImages,

        allowHyperframesScenes,
      })
  }

  const nextPhase =
    needsProductUpload
      ? 'product_input'
      : needsAvatar
        ? 'creating_character'
        : 'generating_scenes'

  input.onProgress?.({
    stage:
      'completed',

    label:
      nextPhase ===
      'creating_character'
        ? 'Storyboard listo. Falta seleccionar avatar.'
        : nextPhase ===
          'product_input'
          ? 'Storyboard listo. Falta el producto.'
          : 'Storyboard listo para revisión.',

    progress:
      100,
  })

  return {
    projectId:
      input.projectId,

    generationId:
      shell.generationId,

    projectJson:
      shell.projectJson,

    premiumStoryboardGeneration,

    preparedStoryboardPlan,

    generatedScenes,

    approvedPlan,

    creativeDirection:
      input.creativeDirection,

    productImageUrl,

    needsAvatar,

    needsProductUpload,

    nextPhase,
  }
}